import React, { useState, useEffect, useContext } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { usePermissions } from '../contexts/PermissionContext';
import UserProfile from './UserProfile';
import NotificationCenter from './NotificationCenter';
import { 
  Menu,
  Calendar,
  ChevronDown,
  LogOut,
  RefreshCw,
  Moon,
  Sun,
  PanelLeftClose,
  PanelLeftOpen,
  User
} from 'lucide-react';
import toast from 'react-hot-toast';

const Navbar = ({ 
  sidebarOpen, 
  setSidebarOpen, 
  sidebarVisible, 
  setSidebarVisible,
  activeDropdown 
}) => {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { logout, user } = useAuth();
  const { companyName } = useSettings();
  const { hasAnyPermission } = usePermissions();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Check if user has any notification permissions
  const hasNotificationPermission = hasAnyPermission([
    'notifications.receive',
    'notifications.otp',
    'notifications.sales',
    'notifications.inventory',
    'notifications.financial',
    'notifications.system'
  ]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuOpen && !event.target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Successfully logged out!', {
        duration: 3000,
        icon: '👋',
        style: {
          background: '#10B981',
          color: '#fff',
          fontSize: '16px',
          padding: '16px',
          borderRadius: '12px',
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed. Please try again.', {
        duration: 3000,
      });
    }
  };

  return (
    <nav className={`fixed top-0 right-0 left-0 z-20 bg-gradient-to-r from-red-500 to-blue-600 rounded-2xl p-2 mb-6 shadow-sm transition-all duration-300 ${sidebarVisible ? 'lg:left-64' : 'lg:left-16'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg text-white/80 hover:bg-white/20 hover:text-white transition-colors"
          >
            <Menu size={20} />
          </button>
          <button
            onClick={() => setSidebarVisible(!sidebarVisible)}
            className="hidden lg:block p-2 rounded-lg text-white/80 hover:bg-white/20 hover:text-white transition-colors"
            title={sidebarVisible ? "Collapse sidebar" : "Expand sidebar"}
          >
            {sidebarVisible ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
          </button>
          <div className="flex items-center space-x-4 text-white/90">
            <div className="flex items-center space-x-2">
              <Calendar size={18} />
              <span className="text-sm font-medium">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
            <div className="hidden md:block">
              <span className="text-sm font-semibold text-white/95">
                {companyName}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Notification Center - Only show if user has notification permissions */}
          {hasNotificationPermission && <NotificationCenter />}
          
          <div className="relative user-menu-container">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center space-x-3 p-2 rounded-lg hover:bg-white/20 transition-colors"
            >
              <div className="w-9 h-9 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white font-bold text-sm border border-white/30 overflow-hidden">
                {user?.avatar_url ? (
                  <img 
                    src={user.avatar_url} 
                    alt={user?.name || 'User'} 
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  (user?.name || user?.username || 'U').charAt(0).toUpperCase()
                )}
              </div>
              <div className="hidden md:block text-right">
                <p className="text-white font-medium">{user?.name || user?.username || 'User'}</p>
              </div>
              <ChevronDown 
                size={16} 
                className={`text-white/70 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} 
              />
            </button>

            {/* User Dropdown Menu */}
            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-60 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
                {/* User Info Section */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg overflow-hidden">
                      {user?.avatar_url ? (
                        <img 
                          src={user.avatar_url} 
                          alt={user?.name || 'User'} 
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        (user?.name || user?.username || 'U').charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-900 font-semibold">{user?.name || user?.username || 'User'}</p>
                      <p className="text-gray-600 text-sm">{user?.email || 'user@example.com'}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <div className="flex items-center space-x-1 bg-green-50 px-2 py-1 rounded-full">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-green-700 text-xs font-medium">Online</span>
                          <button className="ml-2 text-green-600 hover:text-green-700 transition-colors">
                            <RefreshCw size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  <button 
                    onClick={() => {
                      setShowProfileModal(true);
                      setUserMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    <User size={18} className="text-gray-600" />
                    <span className="text-gray-900 font-medium">Profile</span>
                  </button>
                  <button 
                    onClick={toggleTheme}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    {theme === 'light' ? (
                      <Moon size={18} className="text-gray-600" />
                    ) : (
                      <Sun size={18} className="text-gray-600" />
                    )}
                    <span className="text-gray-900 font-medium">
                      {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                    </span>
                  </button>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button 
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={18} className="text-red-500" />
                    <span className="text-red-600 font-medium">Sign out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Profile Modal */}
      <UserProfile 
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
    </nav>
  );
};

export default Navbar;
